import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectActiveMediaDevice } from 'features/user-media/userMediaSlice';
import { AudioPool, UnlockedAudio } from 'utils/audio/AudioPool';

export const useAudio = () => {
  const [attached, setAttached] = useState(false);
  const audioOutputId = useAppSelector((state) => selectActiveMediaDevice(state, 'audiooutput'));
  const audio = useRef<HTMLAudioElement | UnlockedAudio | null>(null);

  const resetAudio = useCallback(() => {
    if (!audio.current) {
      return;
    }

    audio.current.pause();
    audio.current.currentTime = 0;
    audio.current.onplay = null;
    audio.current.onended = null;
    audio.current.onerror = null;
    audio.current.removeAttribute('src');
    audio.current.load();
  }, []);

  useEffect(() => {
    const init = async () => {
      audio.current = await AudioPool.obtainElement();
      if (audioOutputId && audio.current?.setSinkId) {
        try {
          await audio.current.setSinkId(audioOutputId);
        } catch {
          // mute possible DOMException for stale ids during re-rendering
        }
      }
      setAttached(true);
    };

    init();

    return () => {
      if (audio.current) {
        audio.current.pause();
        if (audio.current instanceof UnlockedAudio && !audio.current.unloaded) {
          AudioPool.unloadElement(audio.current);
        }
      }
    };
  }, [audioOutputId]);

  useEffect(() => {
    if (!audio.current) {
      return;
    }

    if (attached && audioOutputId && audio.current.setSinkId) {
      audio.current.setSinkId(audioOutputId).catch(() => {
        // mute possible DOMException for stale ids during re-rendering
      });
    }
  }, [audioOutputId, attached]);

  return {
    attached,
    audio: audio.current,
    resetAudio,
  };
};
